import type { ErrorEvent, EventHint } from '@sentry/react';
import { isChunkReloadPending } from '../lib/chunkReload';
import { isCanceledError } from '../lib/requestCancel';

/**
 * Chromium's older ResizeObserver wording: benign, never actionable. The newer "loop completed with
 * undelivered notifications" wording is already in the SDK's default ignore list.
 */
export const SENTRY_IGNORED_ERRORS: ReadonlyArray<string | RegExp> = ['ResizeObserver loop limit exceeded'];

/** Scripts injected by browser extensions; their errors are not ours. */
export const SENTRY_DENIED_URLS: ReadonlyArray<RegExp> = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
  // Safari masks extension script URLs in stack frames.
  /^webkit-masked-url:\/\//i,
];

/** Chrome/Edge, Firefox, Safari and Vite wordings of a failed lazy-route chunk load. */
export const CHUNK_LOAD_FAILURE_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

function errorMessageOf(event: ErrorEvent, hint?: EventHint): string {
  const original = hint?.originalException;
  if (original instanceof Error) return original.message;
  if (typeof original === 'string') return original;
  return event.exception?.values?.[0]?.value ?? '';
}

/**
 * Drops events that are expected and already handled:
 * - cancellations (aborted request, cancelled query, DOM abort: the caller stopped on purpose);
 * - a chunk-load failure while the chunk-reload handler has a reload in flight. A chunk failure
 *   with no reload pending (the reload did not fix it) is a real problem and stays reported.
 */
export function filterSentryEvent(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  if (isCanceledError(hint?.originalException)) return null;
  if (isChunkReloadPending() && CHUNK_LOAD_FAILURE_PATTERN.test(errorMessageOf(event, hint))) {
    return null;
  }
  return event;
}
